/// <reference types="vite/client" />

import { TooltipProvider } from "@radix-ui/react-tooltip";
import { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useMatches,
} from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import dedent from "dedent";
import { useEffect } from "react";

import { ErrorPage, RedirectErrorPage } from "@/components/ErrorPage";
import { Toaster } from "@/components/ui/toaster";
import { productName } from "@/lib/constants";
import appCss from "@/styles.css?url";

function NotFoundComponent({ data }: { data: unknown }) {
  useEffect(() => {
    console.log("NotFoundComponent", data);
  }, [data]);
  return <ErrorPage title="404" description="Page not found" />;
}

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  component: RootComponent,
  head: () => {
    return {
      meta: [{ title: productName }],
      links: [{ rel: "stylesheet", href: appCss }],
    };
  },
  notFoundComponent: NotFoundComponent,
  errorComponent: RedirectErrorPage,
  scripts: () => {
    return [
      {
        src: "https://plausible.io/js/script.js",
        defer: true,
        "data-domain": "graze.social",
      },
      {
        async: true,
        src: "https://www.googletagmanager.com/gtag/js?id=AW-17030367179",
      },
      {
        type: "text/javascript",
        children: dedent`
          window.dataLayer = window.dataLayer || [];
          function gtag() {
            dataLayer.push(arguments);
          }
          gtag("js", new Date());
        `,
      },
      {
        type: "text/javascript",
        // Support chat belongs to us, not to whoever embedded a feed on their
        // own site. The check has to happen here at run time rather than on
        // the route match: `scripts` runs on the root route, whose pathname is
        // always "/" no matter which page was requested.
        children: dedent`
          if (!window.location.pathname.includes("/feeds/embed/")) {
            window.$crisp = [];
            window.CRISP_WEBSITE_ID = "8de5673c-8527-4058-b32c-1d3538854570";
            (function () {
              d = document;
              s = d.createElement("script");
              s.src = "https://client.crisp.chat/l.js";
              s.async = 1;
              d.getElementsByTagName("head")[0].appendChild(s);
            })();
          }
        `,
      },
    ];
  },
});

function Meta({ children }: { children: React.ReactNode }) {
  const matches = useMatches();
  const meta = matches.at(-1)?.meta?.find((meta) => meta?.title);

  useEffect(() => {
    document.title = [meta?.title, productName].filter(Boolean).join(" · ");
  }, [meta]);

  return children;
}

function RootComponent() {
  return (
    <Meta>
      <TooltipProvider>
        <html lang="en">
          <head>
            <meta charSet="utf-8" />
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0"
            />
            <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
            <link
              rel="alternate"
              type="application/rss+xml"
              title="Graze Blog"
              href={new URL(
                "/blog/rss.xml",
                "https://www.graze.social",
              ).toString()}
            />
            <link rel="sitemap" href="/sitemap.xml" />
            <HeadContent />
          </head>
          <body>
            <Outlet />
            <Toaster />
            <Scripts />

            {process.env.NODE_ENV === "development" &&
              !import.meta.env.VITE_DISABLE_DEVTOOLS && (
                <>
                  <TanStackRouterDevtools position="bottom-right" />
                  <ReactQueryDevtools initialIsOpen={false} />
                </>
              )}
          </body>
        </html>
      </TooltipProvider>
    </Meta>
  );
}
